import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

type Primitive = string | number | boolean;

/** 목록 화면 상태가 실리는 URL search — 라우트 validateSearch 가 그대로 통과시킨다 */
export type ListViewSearch = Record<string, Primitive | undefined>;

/** validateSearch 용 — 원시값만 남긴다 (목록 상태 키는 화면마다 달라 키를 고정하지 않는다) */
export function keepListView(search: Record<string, unknown>): ListViewSearch {
  const kept: ListViewSearch = {};
  for (const [key, value] of Object.entries(search)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      kept[key] = value;
    }
  }
  return kept;
}

type Navigate = ReturnType<typeof useNavigate>;
type ListViewState<T> = [T, (next: T | ((prev: T) => T)) => void];

// 한 핸들러에서 여러 값을 바꿔도(필터 + 페이지 초기화) 이동은 한 번만 — 마이크로태스크로 모아 반영
let pendingPatch: ListViewSearch | null = null;

export function queuePatch(navigate: Navigate, patch: ListViewSearch): void {
  if (!pendingPatch) {
    pendingPatch = {};
    queueMicrotask(() => {
      const merged = pendingPatch ?? {};
      pendingPatch = null;
      void navigate({
        to: '.',
        search: ((prev: ListViewSearch) => ({ ...prev, ...merged })) as never,
        replace: true,
      });
    });
  }
  Object.assign(pendingPatch, patch);
}

/**
 * useState 대신 쓰는 목록 화면 상태 (페이지·필터·검색어·정렬).
 *
 * 값을 URL search 에 둬서, 상세로 들어갔다 뒤로 나와도 보던 페이지·필터가 그대로 복원된다.
 * 기본값과 같으면 URL 에서 뺀다. 기록은 replace 라 뒤로가기 한 번이면 이전 화면으로 간다.
 */
export function useListViewState(name: string, initial: number): ListViewState<number>;
export function useListViewState(name: string, initial: string): ListViewState<string>;
export function useListViewState(name: string, initial: boolean): ListViewState<boolean>;
export function useListViewState<T extends Primitive>(name: string, initial: T): ListViewState<T>;
export function useListViewState<T extends Primitive>(name: string, initial: T): ListViewState<T> {
  const search = useSearch({ strict: false }) as ListViewSearch;
  const navigate = useNavigate();

  const raw = search[name];
  const value = typeof raw === typeof initial ? (raw as T) : initial;

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved = typeof next === 'function' ? next(value) : next;
      queuePatch(navigate, { [name]: resolved === initial ? undefined : resolved });
    },
    [navigate, name, initial, value],
  );
  return [value, setValue];
}
